import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { swalSuccess } from "../utils/swalAlert";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const initialState = {
  isAuthenticated: false,
  user: null,
  primaryRole: null,
  loading: true,
};

export const AuthContext = createContext(initialState);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [primaryRole, setPrimaryRole] = useState(null);

  // Check for existing auth on mount using API.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userResponse = await axios.get(
          `${serverUrl}/api/user/signed-in`,
          { withCredentials: true }
        );
        if (
          userResponse.status === 200 &&
          userResponse.data &&
          userResponse.data._id
        ) {
          let category =
            userResponse.data.category ||
            userResponse.data.institute?.category ||
            "K12_SCHOOL";
          setUser({ ...userResponse.data, category });
          setIsAuthenticated(true);

          const storedRole = localStorage.getItem("primaryRole");
          const availableRoles = userResponse.data.role || [];

          // Prefer stored role if it's still available.
          if (storedRole && availableRoles.includes(storedRole)) {
            setPrimaryRole(storedRole);
          } else {
            // Stable priority fallback (don't trust server array order)
            const priority = ["instructor", "individual", "student", "admin"];
            const picked =
              priority.find((r) => availableRoles.includes(r)) ||
              availableRoles[0] ||
              null;
            setPrimaryRole(picked);
            if (picked) {
              localStorage.setItem("primaryRole", picked);
            }
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("primaryRole"); // Clear stored role on logout
    setUser(null);
    setIsAuthenticated(false);
    setPrimaryRole(null);
  };

  // Function to switch roles
  const switchRole = useCallback(
    (newRole) => {
      setPrimaryRole(newRole);
      localStorage.setItem("primaryRole", newRole); // Store the new role
      swalSuccess(
        `You have switched to the ${
          newRole.charAt(0).toUpperCase() + newRole.slice(1)
        } role.`
      );
    },
    [setPrimaryRole]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        primaryRole,
        setPrimaryRole,
        loading,
        logout,
        switchRole,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
